import React, { forwardRef, PropsWithoutRef, SyntheticEvent, useCallback } from 'react'

import { setFindingMethod, FindingMethodType } from '@src/telemetry/findingMethod'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { TelemetryEventNames } from '@src/telemetry/telemetry-events.types'
import { trackClickEvent } from '@src/telemetry/track'

export interface TrackConfig {
  trackInternal?: boolean
  trackGA?: boolean
  category: string
  action: string
  param?: string | undefined
  context?: Record<string, unknown>
  value?: string | undefined
  label?: string | undefined
}

export interface WithTrackingProps {
  trackConfig?: TrackConfig
  onClick?: (...args: any[]) => any
}

const trackEvent = (trackConfig: TrackConfig, eventType: TelemetryEventNames) => {
  trackClickEvent(eventType, trackConfig)
}

function withTracking<X, T extends X & WithTrackingProps>(
  WrappedComponent: React.ComponentType<X>,
  shouldWrapWithRef: boolean,
) {
  const displayName = WrappedComponent.displayName ?? (WrappedComponent.name || 'Component')
  function WithTracking(props: PropsWithoutRef<T>, ref: React.Ref<any>) {
    const { onClick, trackConfig } = props
    const onClickOverride = useCallback(
      (event: SyntheticEvent, ...args: any[]) => {
        let eventType = TELEMETRY_EVENT_NAMES.LINK_CLICK
        if (onClick) {
          eventType = TELEMETRY_EVENT_NAMES.BUTTON_CLICK
          onClick(event, ...args)
        }
        if (trackConfig) {
          // Stopped event propagation to prevent duplicate event firing.
          event.stopPropagation()
          trackEvent(trackConfig, eventType)
          setFindingMethod[trackConfig.context?.findingMethod as FindingMethodType]?.({
            elementName: trackConfig.context?.contentContainerName || '',
            elementId: trackConfig.context?.trackingId || '',
          })
        }
      },
      [trackConfig, onClick],
    )

    WrappedComponent.displayName = `withTracking(${displayName})`

    const WrappedComponentJSX = WrappedComponent as (props: X) => JSX.Element // JUST TS FIX

    return <WrappedComponentJSX {...(props as X)} onClick={onClickOverride} ref={ref} />
  }
  if (typeof shouldWrapWithRef !== 'boolean') {
    throw new Error('Should specificy if wrapping needs ref')
  }
  if (!shouldWrapWithRef) {
    return WithTracking
  }
  return forwardRef(WithTracking)
}

export default withTracking
